import { EventEmitter, Inject, Injectable } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { SVG, Svg, Image, Text as SvgText } from '@svgdotjs/svg.js';
import { ColorPaletteService
       } from 'app/controls/color-chooser/color-palette.service';
import { ImageQualityRatingService
       } from 'app/controls/image-quality-rating/image-quality-rating.service';
import { GalleryService } from 'app/gallery/gallery.service';
import { ImageProvisioningService } from 'app/gallery/imageprovider.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { FontInjectorService } from 'collage-designer/font-injector.service';
import { GlobalConstants, StageConstants } from 'core/constants';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { TraudlService } from 'core/traudl/traudl.service';
import { StageUtils } from 'core/utils.stage';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap
       } from 'rxjs/operators';
import { DesignStepService } from '../../design-step/design-step.service';
import { ActionBarDirective } from './action-bar/action-bar.directive';
import { ImagePlaceholder } from './image-placeholder';
import { Placeholder, PlaceholderType } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { TextPlaceholder } from './text-placeholder';

interface PlaceholdersListItem
{
  domId: string;
  type: PlaceholderType;
  placeholder: Placeholder;
}

@Injectable()
export class PlaceholderService
{
  public placeholderChanged: EventEmitter<Placeholder> = new EventEmitter();
  public focusedPlaceholderChanged: EventEmitter<Placeholder|undefined>
      = new EventEmitter();

  private placeholders: Array<PlaceholdersListItem> = [];
  private placeholderChangedSubscriptions: Subscription = new Subscription();
  private actionBars: ActionBarDirective;
  private focusedPlaceholder: Placeholder|undefined = undefined;

  constructor(
      @Inject(ErrorService) private errorService: ErrorService,
      @Inject(LoggingService) private loggingService: LoggingService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(ImageQualityRatingService) private imageQualityRatingService:
          ImageQualityRatingService,
      @Inject(ImageProvisioningService) private imageProvisioningService,
      @Inject(ImageStoreService) private imageStoreService,
      @Inject(TraudlService) private traudlService,
      @Inject(FontInjectorService) private fontInjectorService,
      @Inject(DesignStepService) private designStepService,
      @Inject(ColorPaletteService) private colorPaletteService,
      @Inject(ToolboxService) private toolboxService,
      @Inject(PlaceholderContentService) private placeholderContentService,
      private eventTrackingService: EventTrackingService,
      private productService: ProductService,
      private dialog: MatDialog,
  )
  { }

  public async refreshPlaceholders(
      svgReference: Svg,
      actionBars: ActionBarDirective): Promise<void>
  {
    this.actionBars = actionBars;
    const document = svgReference.node;

    await Promise.all(this.placeholders.map(i => i.placeholder.destructor()));

    this.placeholders = [];
    this.cancelChangeSubscriptions();

    // We must determine all placeholders before we create them, otherwise
    // we could accidentally create sub-placeholders. This would lead to errors.
    const images = [].slice
        .call(document.querySelectorAll('image'))
        .filter(element => !StageUtils.isAncestorLinked(
            <Node> StageUtils.findParentBySelector(element, 'svg')))
        .filter(StageUtils.containerHasZoomMode);

    const texts = [].slice
        .call(document.querySelectorAll('text'))
        .filter(element => !StageUtils.isAncestorLinked(
            <Node> StageUtils.findParentBySelector(element, 'svg')))
        .filter(StageUtils.containerHasZoomMode);

    await Promise.all(
        images.map(i => this.createImagePlaceholder(SVG(i) as Image))
          .concat(
              texts.map(t => this.createTextPlaceholder(
                  SVG(t) as SvgText))));
  }

  private cancelChangeSubscriptions(): void
  {
    this.placeholderChangedSubscriptions.unsubscribe();
    this.placeholderChangedSubscriptions = new Subscription();
  }

  public getChangeablePlaceholders(): Array<PlaceholdersListItem>
  {
    return this.placeholders.filter(p => p.placeholder.isContentChangeable());
  }

  public getImagePlaceholders(onlyChangeable: boolean = true):
      Array<ImagePlaceholder>
  {
    const placeholders =
        onlyChangeable ? this.getChangeablePlaceholders() : this.placeholders;

    return placeholders
      .filter(item => item.type === PlaceholderType.Image)
      .map(item => <ImagePlaceholder> item.placeholder)
      .filter(item => item.element.node.parentNode);
  }

  public getTextPlaceholders(onlyChangeable: boolean = true):
      Array<TextPlaceholder>
  {
    const placeholders =
        onlyChangeable ? this.getChangeablePlaceholders() : this.placeholders;

    return placeholders
      .filter(item => item.type === PlaceholderType.Text)
      .map(item => <TextPlaceholder> item.placeholder);
  }

  public getSoleTextPlaceholder(): TextPlaceholder|undefined
  {
    const isCreateNewAllowed = this.designStepService.getDesignStepConfig()
        .textPlaceholdersSettings.showCreateNewButton;
    const textPlaceholders = this.getTextPlaceholders();

    return !isCreateNewAllowed && textPlaceholders.length === 1
        ? textPlaceholders[0]
        : undefined;
  }

  public resetPendingPlaceholders(): void
  {
    const pendingPlaceholders = this.placeholders
      .filter(item => item.placeholder.isPending() === true)
      .map(item => item.placeholder);

    for (let item of pendingPlaceholders) {
      item.set(StageConstants.ATTRIBUTE_PENDING, undefined);
    }
  }

  private async changeableImagePlaceholdersAvailable(): Promise<boolean>
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    const placeholders =
        (await this.designStepService.getTemplate()).imagePlaceholders;
    return (placeholders.portrait + placeholders.landscape) > 0;
  }

  private async createImagePlaceholder(
      svgImage: Image): Promise<ImagePlaceholder>
  {
    const isLocked = !(await this.changeableImagePlaceholdersAvailable());

    const placeholder = new ImagePlaceholder(
        this.errorService,
        this.loggingService,
        this.designStepService,
        this.galleryService,
        this.imageQualityRatingService,
        this.imageProvisioningService,
        this.imageStoreService,
        this.traudlService,
        this.toolboxService,
        this.placeholderContentService,
        this,
        this.eventTrackingService,
        this.productService,
        svgImage,
        this.designStepService.getCurrentDesignState().partId,
        isLocked,
        this.dialog,
    );
    await placeholder.setup();

    this.addPlaceholderToPlaceholders(placeholder);
    return placeholder;
  }

  public async createTextPlaceholder(
      svgText: SvgText,
      clickAfterModelSet: boolean = false): Promise<TextPlaceholder>
  {
    const placeholder = new TextPlaceholder(
        this.errorService,
        this.loggingService,
        this.designStepService,
        this.galleryService,
        this.fontInjectorService,
        this.colorPaletteService,
        this.toolboxService,
        this.placeholderContentService,
        this,
        this.eventTrackingService,
        this.productService,
        this.actionBars,
        svgText,
        this.designStepService.getCurrentDesignState().partId,
        this.dialog
    );
    await placeholder.setup(clickAfterModelSet);

    this.addPlaceholderToPlaceholders(placeholder);
    return placeholder;
  }

  private addPlaceholderToPlaceholders(placeholder: Placeholder): void
  {
    this.placeholders.push(<PlaceholdersListItem>{
      domId: placeholder.element.id(),
      type: placeholder.type,
      placeholder: placeholder
    });
    this.placeholderChangedSubscriptions.add(
        placeholder.changed.subscribe(
            () => this.placeholderChanged.emit(placeholder)));
    this.placeholderChangedSubscriptions.add(
        placeholder.destroyed.subscribe(
            () => this.placeholders = this.placeholders.filter(
                pItem => pItem.placeholder !== placeholder)));
  }

  public getUnchangedPlaceholders(onlyChangeable: boolean = true):
      Array<PlaceholdersListItem>
  {
    if (onlyChangeable) {
      return this.placeholders.filter(p =>
          p.placeholder.isUnchanged() && p.placeholder.isContentChangeable());
    }
    else {
      return this.placeholders.filter(p => p.placeholder.isUnchanged());
    }
  }

  public async clickAnyUnchangedImagePlaceholder(): Promise<void>
  {
    const placeholders = this.getUnchangedPlaceholders()
      .filter(item => item.type === PlaceholderType.Image)
      .map(item => <ImagePlaceholder> item.placeholder);
    if (placeholders.length < 1) {
      return;
    }
    await placeholders[0].clickEvent();
  }

  public async clickAnyUnchangedTextPlaceholder(): Promise<void>
  {
    const placeholders = this.getUnchangedPlaceholders()
      .filter(item => item.type === PlaceholderType.Text)
      .map(item => <TextPlaceholder> item.placeholder);
    if (placeholders.length < 1) {
      return;
    }
    await placeholders[0].clickEvent();
  }

  public async clickTitleTextPlaceholder(): Promise<void>
  {
    const placeholders = this.placeholders
      .filter(item => item.type === PlaceholderType.Text &&
              item.placeholder.element.id()
                  === StageConstants.BOX_TITLE_TEXT_ID)
      .map(item => <TextPlaceholder> item.placeholder);
    if (placeholders.length < 1) {
      return;
    }
    await placeholders[0].clickEvent();
  }

  public hasTransformHandle(): boolean
  {
    return this.placeholders.some(
        pItem => pItem.placeholder.hasTransformHandle());
  }

  public disableTextPlaceholders(): void
  {
    if (this.getFocusedPlaceholder() instanceof TextPlaceholder) {
      this.setFocusedPlaceholder(undefined);
    }
    for (let tph of this.getTextPlaceholders(true)) {
      tph.suspendPointerEvents();
    }
  }

  public enableTextPlaceholders(): void
  {
    for (let tph of this.getTextPlaceholders(true)) {
      tph.resumePointerEvents();
    }
  }

  public getFocusedPlaceholder(): Placeholder|undefined
  {
    return this.focusedPlaceholder;
  }

  public setFocusedPlaceholder(placeholder: Placeholder|undefined): void
  {
    if (placeholder === this.focusedPlaceholder) {
      return;
    }
    if (this.focusedPlaceholder) {
      this.focusedPlaceholder.blur();
    }
    this.focusedPlaceholder = placeholder;
    this.focusedPlaceholderChanged.emit(placeholder);
  }

  public get imageBeingEdited(): Observable<ImagePlaceholder>
  {
    return this.focusedPlaceholderChanged.pipe(
        startWith(this.focusedPlaceholder),
        filter((placeholder): placeholder is ImagePlaceholder =>
            placeholder instanceof ImagePlaceholder),
        distinctUntilChanged(),
        switchMap((placeholder) =>
            placeholder.transformationManager.changed.pipe(
                map(() => placeholder))));
  }

  public getActivePlaceholderToolbox(): string|undefined
  {
    return this.toolboxService.getActiveToolboxName('placeholder');
  }

  public showQualityIndicators(show: boolean): void
  {
    this.getImagePlaceholders().forEach(placeholder => show
          ? placeholder.showQualityIndication()
          : placeholder.hideQualityIndication());
  }

  public areQualityIndicatorsVisible(): boolean
  {
    return this.getImagePlaceholders().some(p => p.qualityIndicatorGroup);
  }
}
